import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import WebsiteLogo from "@/components/common/WebsiteLogo";
import { app } from "@/services/firebaseService";

/**
 * KB-2361: bounds the enforced-App-Check callable attempt so a wedged mint
 * (KB-1087 class — the promise never settles) converts into the standard
 * network-error surface instead of wedging the form forever. Ported from
 * marketing's src/lib/reset-timeout.ts (KB-1223) — pro cannot import across
 * repos. The rejection carries `code: 'functions/deadline-exceeded'`, which
 * humanize() below maps to the network-error copy.
 */
async function raceWithResetTimeout<T>(attempt: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      attempt,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () =>
            reject(
              Object.assign(new Error("Password reset request timed out"), {
                code: "functions/deadline-exceeded",
              }),
            ),
          ms,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function humanize(code: string, fallback: string | undefined): string {
  switch (code) {
    case "functions/invalid-argument":
    case "auth/invalid-email":
      return "That doesn’t look like a valid email address.";
    case "functions/resource-exhausted":
    case "auth/too-many-requests":
      return "Too many attempts. Try again in a few minutes.";
    case "functions/unavailable":
    case "functions/deadline-exceeded":
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return fallback || "Something went wrong. Please try again.";
  }
}

const ForgotPasswordPage = ({
  // KB-2361: test seam only — production renders use the default. Bounds the
  // enforced-App-Check attempt so a wedged mint can't wedge this form forever.
  resetTimeoutMs = 15_000,
}: {
  resetTimeoutMs?: number;
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Extract the returnUrl from query parameters to pass it back to login page
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl") || "";

  const loginPath = returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : "/login";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // KB-2603: fire the pro `authSendPasswordResetEmail` callable — same
      // getFunctions(app, 'us-east4') + httpsCallable idiom as `syncMyClaims`
      // in AuthContext.tsx. App Check is initialised at module load in
      // firebaseService.ts, so mint ordering is already handled here.
      const functions = getFunctions(app, "us-east4");
      const sendReset = httpsCallable<
        { email: string; continueUrl: string },
        { ok: boolean }
      >(functions, "authSendPasswordResetEmail");
      await raceWithResetTimeout(
        sendReset({
          email: email.trim(),
          // KB-2603 round 2 (code-review high): MUST be derived from the
          // running origin, not hardcoded to prod. BUILTIN_TRUSTED_HOSTS is
          // one module constant shared by every deployment (prod AND
          // staging both run this same compiled file) — a hardcoded
          // "https://pro.kidsbooked.com/login" meant a staging tester's
          // reset link redirected them to PRODUCTION pro after completing
          // the reset, where their staging credentials don't exist. The
          // origin must still match a host in BUILTIN_TRUSTED_HOSTS or
          // coerceTrustedContinueUrl() silently falls back to
          // DEFAULT_CONTINUE and the user lands on the consumer app instead.
          continueUrl: `${window.location.origin}/login`,
        }),
        resetTimeoutMs,
      );
      // Privacy-safe: the callable always resolves ok for a well-formed
      // email, whether or not the address maps to an account.
      setIsSubmitted(true);
    } catch (error: any) {
      // KB-2603 /preempt high H1/H2: `error?.message ?? String(error)` looked
      // like a safe fallback but String({}) is the truthy, useless
      // "[object Object]" — humanize()'s `fallback || "..."` would then ship
      // THAT to the user instead of the generic message. Pass through only a
      // real message; every real rejection here (the callable's FunctionsError,
      // or raceWithResetTimeout's own Error) already carries one.
      const code: string = error?.code ?? "";
      toast.error(humanize(code, error?.message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-kidsbooked-lightGray">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <WebsiteLogo width={180} className="mb-4" />
          <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
          {!isSubmitted ? (
            <CardDescription>
              Enter your email address and we'll send you a link to reset your password
            </CardDescription>
          ) : (
            <CardDescription>
              Check your email for the reset link
            </CardDescription>
          )}
        </CardHeader>
        {!isSubmitted ? (
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email address
                </label>
                <Input
                  id="email"
                  placeholder="name@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </CardContent>
        ) : (
          <CardContent className="text-center py-8">
            <div className="mb-6 bg-green-50 text-green-700 p-4 rounded-md">
              If an account exists for that address, we've sent a password reset link. The link expires in 1 hour.
            </div>
            <Button
              onClick={() => navigate(loginPath)}
              className="btn-primary"
            >
              Back to Login
            </Button>
          </CardContent>
        )}
        <CardFooter className="justify-center">
          <p className="text-sm text-muted-foreground">
            Remember your password?{" "}
            <Link to={loginPath} className="text-kidsbooked-blue hover:underline">
              Login
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
